// Shared by the student's competition pages: the card list and the tab row on
// the overview page must order competitions the same way.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// A deadline is a calendar date, not a moment. Parsing it with `new Date()`
// would read it as UTC midnight and show the previous day to students west of
// UTC, so the parts are read directly.
export const parseDateOnly = (value) => {
  if (!value) return null;
  const [y, m, d] = String(value).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

// Overview content will come from a parsed PDF, so a link is only rendered if it
// is a plain http(s) URL (never `javascript:` or anything else).
export const isHttpUrl = (url) => typeof url === 'string' && /^https?:\/\//i.test(url);

export const formatDeadline = (date) =>
  `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;

export const daysUntil = (date, today) => Math.round((date - today) / 86400000);

export const daysChip = (days) => {
  if (days < 0) return { text: 'Deadline passed', tone: 'passed' };
  if (days === 0) return { text: 'Due today', tone: 'urgent' };
  const text = `T-${days} ${days === 1 ? 'Day' : 'Days'}`;
  return { text, tone: days <= 7 ? 'urgent' : 'normal' };
};

/**
 * Turn the enrollments from GET /competition-workflow/me/enrollments into the
 * ordered list the student sees.
 *
 * The deadline is the competition's official `final_deadline`, not the
 * student's own internal due date.
 *
 * Order: upcoming deadlines first (nearest first), then competitions with no
 * deadline, then those whose deadline has passed (most recent first).
 *
 * `priorityId` is the enrollment that gets "Prioritise next": the nearest
 * upcoming deadline that still has work left, so a finished competition never
 * takes it.
 */
export const buildCompetitionCards = (enrollments) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const list = (enrollments || []).map((e) => {
    const date = parseDateOnly(e.competition_templates?.final_deadline);
    return {
      id: e.id,
      templateId: e.template_id,
      name: e.competition_templates?.name || 'Competition',
      date,
      days: date ? daysUntil(date, today) : null,
      status: e.card_status || 'not_started',
      nextStep: e.next_step?.title || null,
    };
  });

  // 0 = upcoming, 1 = no deadline, 2 = passed.
  const group = (c) => (c.days === null ? 1 : c.days >= 0 ? 0 : 2);
  list.sort((a, b) => {
    const ga = group(a);
    const gb = group(b);
    if (ga !== gb) return ga - gb;
    if (ga === 0) return a.days - b.days || a.name.localeCompare(b.name);
    if (ga === 2) return b.days - a.days || a.name.localeCompare(b.name);
    return a.name.localeCompare(b.name);
  });

  const priority = list.find((c) => c.days !== null && c.days >= 0 && c.status !== 'approved');
  return { cards: list, priorityId: priority ? priority.id : null };
};
